import { inject, injectable } from 'inversify';
import { IMapper } from '@Mapper/IMapper';
import { ArmyListUnitMapper } from '@Mapper/builder/army-list-unit.mapper';
import { TYPES } from '@Types';
import {
  RequestAction,
  RequestFailure,
  RequestSuccess,
} from '../RequestAction';
import { RequestArmyListUnitSuccess } from './response';
import { ArmyListUnit, Nullable } from '@Models';
import { ApiClient } from '@Api';
import { DeleteRepository } from '../DeleteRepository';
import CacheRepository from '../CacheRepository';
import { army_list_unit } from '@Api';

@injectable()
export class ArmyListUnitRepository
  extends CacheRepository<string, army_list_unit, army_list_unit, ArmyListUnit>
  implements DeleteRepository<string>
{
  @inject(TYPES.ArmyListUnitMapper)
  private _armyListUnitMapper!: ArmyListUnitMapper;
  @inject(TYPES.Api.ApiClient) private _apiClient!: ApiClient;

  constructor() {
    super({ key: `/builder/army_list_units` });
  }

  public async create({
    id,
    armyListUnit,
  }: {
    id: string;
    armyListUnit: ArmyListUnit;
  }): Promise<RequestAction> {
    try {
      const armyListUnitRequestBody =
        this._armyListUnitMapper.mapDto(armyListUnit);

      const armyDto = await this._apiClient.builder.createArmyListUnit({
        locale: this._locale.getlocale(),
        id: id,
        requestBody: armyListUnitRequestBody,
      });

      return new RequestArmyListUnitSuccess({
        payload: this._armyListUnitMapper.map(armyDto),
      });
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async update({
    armyListUnit,
  }: {
    armyListUnit: ArmyListUnit;
  }): Promise<RequestAction> {
    try {
      const armyListUnitRequestBody =
        this._armyListUnitMapper.mapDto(armyListUnit);

      const armyDto = await this._apiClient.builder.updateArmyListUnit({
        locale: this._locale.getlocale(),
        armyListUnitId: armyListUnit.id!!,
        requestBody: armyListUnitRequestBody,
      });

      return new RequestArmyListUnitSuccess({
        payload: this._armyListUnitMapper.map(armyDto),
      });
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async delete({
    id,
    locale,
  }: {
    id: string;
    locale?: Nullable<string>;
  }): Promise<RequestAction> {
    try {
      await this._apiClient.builder.deleteArmyListUnit({
        locale: locale ?? this._locale.getlocale(),
        armyListUnitId: id,
      });

      return new RequestSuccess();
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  public async getArmyListUnit({
    id,
    locale,
  }: {
    id: string;
    locale: Nullable<string>;
  }) {
    try {
      const armyListDto = await this._apiClient.builder.getArmyListUnit({
        locale: locale ?? this._locale.getlocale(),
        armyListUnitId: id,
      });

      await this.setCachedEntity({ data: armyListDto, locale: locale });
      return new RequestArmyListUnitSuccess({
        payload: this._armyListUnitMapper.map(armyListDto),
      });
    } catch (error: any) {
      return new RequestFailure({ error: error });
    }
  }

  protected getMapper(): IMapper<
    string,
    army_list_unit,
    army_list_unit,
    ArmyListUnit
  > {
    return this._armyListUnitMapper;
  }
}
